import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, onSnapshot, collection } from 'firebase/firestore';
import { db } from './firebase';
import { isIOSShell } from './components/auth/AuthUtils';
import { hasComplimentaryExpiredMarker } from './utils/subscriptionState';
import { refreshSubscriptionStatusSuperProperty } from './utils/analytics';
import { clearGuestMedications } from './utils/guestMedications';
import { syncCheckerPicksWithUser } from './utils/interactionCheckerStore';

const AuthContext = createContext();

const EMPTY_COMPLIMENTARY_ACCESS = { expired: false, endedAt: null, until: null };

// Firestore Timestamp | Date | ISO string → Date (or null)
const toDateOrNull = (value) => {
  try {
    if (!value) return null;
    if (typeof value.toDate === 'function') return value.toDate();
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch (_) {
    return null;
  }
};

// Module-level flag to persist across React StrictMode re-mounts
let isAuthInitialized = false;
let persistedUser = null;

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  // Initialize with persisted user if available
  const [currentUser, setCurrentUser] = useState(persistedUser);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [isGrandfathered, setIsGrandfathered] = useState(false);
  // Complimentary-expiry marker from the user doc (see utils/subscriptionState)
  const [complimentaryAccess, setComplimentaryAccess] = useState(EMPTY_COMPLIMENTARY_ACCESS);
  // True when the user-doc listener errored: state resolvers must treat the
  // grant/marker as unknown rather than assuming it is absent.
  const [userDocError, setUserDocError] = useState(false);
  const [loading, setLoading] = useState(!persistedUser); // Don't load if we have persisted user

  // Use ref to track cleanup functions
  const firestoreCleanupRef = useRef(null);
  const authUnsubscribeRef = useRef(null);
  // Latest listener values for cross-listener decisions (analytics precedence,
  // grandfathered revocation detection) without stale closures.
  const prevGrandfatheredRef = useRef(null);
  const analyticsInputsRef = useRef({ userData: null, stripeStatus: null });

  useEffect(() => {
    // Prevent duplicate initialization
    if (isAuthInitialized && persistedUser) {
      return;
    }
    
    const auth = getAuth();
    
    // Set a timeout to ensure loading never stays true forever
    const loadingTimeout = setTimeout(() => {
      setLoading(false);
    }, 10000); // 10 second timeout

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      // Clear the loading timeout since we got a response
      clearTimeout(loadingTimeout);
      
      // Clean up any existing Firestore listeners
      if (firestoreCleanupRef.current) {
        firestoreCleanupRef.current();
        firestoreCleanupRef.current = null;
      }
      
      if (user) {
        // A logged-out visitor's medications are never carried into an
        // account: signing in or up empties the tab store (STACK-565;
        // docs/guest-medication-check.md §3, step 6).
        clearGuestMedications();
        // The interaction checker page's picks go the same way (STACK-587;
        // docs/supplement-interaction-checker.md §3, step 8) — but not when
        // this is the same user's session restored on reload.
        syncCheckerPicksWithUser(user.uid);

        // Persist user and mark as initialized
        persistedUser = user;
        isAuthInitialized = true;
        
        // Set user state
        setCurrentUser(user);
        
        try {
          // Check if user is grandfathered
          const userDocRef = doc(db, 'users', user.uid);
          
          const userUnsubscribe = onSnapshot(userDocRef, (doc) => {
            const userData = doc.exists() ? doc.data() : {};
            const grandfathered = userData.isGrandfathered || false;
            setUserDocError(false);
            setIsGrandfathered(grandfathered);
            setComplimentaryAccess({
              expired: hasComplimentaryExpiredMarker(userData),
              endedAt: toDateOrNull(userData.grandfatheredEndedAt),
              until: toDateOrNull(userData.grandfatheredUntil),
            });

            // Complimentary access revoked mid-session (migration): refresh
            // the JWT so the hasSubscription claim converges without waiting
            // up to 60 min for natural token expiry. 3s delay gives the
            // onUserWrite Cloud Function time to finish setCustomUserClaims.
            if (prevGrandfatheredRef.current === true && !grandfathered) {
              setTimeout(() => {
                getAuth().currentUser?.getIdToken(true).catch(() => {});
              }, 3000);
            }
            prevGrandfatheredRef.current = grandfathered;

            // Keep the Mixpanel super property in sync with the user doc so a
            // migrated session doesn't stay tagged 'grandfathered'.
            analyticsInputsRef.current.userData = userData;
            refreshSubscriptionStatusSuperProperty(
              analyticsInputsRef.current.userData,
              analyticsInputsRef.current.stripeStatus
            );
          }, (error) => {
            console.error('Error in user document listener:', error);
            // Unknown, not false: never downgrade off a failed read.
            setUserDocError(true);
            setIsGrandfathered(false);
            setComplimentaryAccess(EMPTY_COMPLIMENTARY_ACCESS);
          });

          // Listen to subscription changes in real-time
          const subscriptionsRef = collection(db, 'customers', user.uid, 'subscriptions');

          const normalizeToSeconds = (val) => {
            try {
              if (val == null) return null;
              if (typeof val === 'number') return val; // already seconds
              if (typeof val === 'string' && /^\d+$/.test(val)) return parseInt(val, 10);
              if (val && typeof val === 'object') {
                if (typeof val.seconds === 'number') return val.seconds;
                if (typeof val.toDate === 'function') return Math.floor(val.toDate().getTime() / 1000);
              }
            } catch (_) {}
            return null;
          };

          const pickBestSubscription = (docs) => {
            if (!docs || docs.length === 0) return null;
            const subs = docs.map(d => ({ id: d.id, ...d.data() }));

            const isProStatus = (s) => s === 'active' || s === 'trialing';

            // Prefer active/trialing; among them, pick the one with latest current_period_end, then latest created
            const proSubs = subs.filter(s => isProStatus(s.status));
            const byLatest = (a, b) => {
              const aEnd = normalizeToSeconds(a.current_period_end) || 0;
              const bEnd = normalizeToSeconds(b.current_period_end) || 0;
              if (aEnd !== bEnd) return bEnd - aEnd;
              const aCreated = normalizeToSeconds(a.created) || 0;
              const bCreated = normalizeToSeconds(b.created) || 0;
              return bCreated - aCreated;
            };

            if (proSubs.length > 0) {
              return proSubs.sort(byLatest)[0];
            }
            // Otherwise, pick the most recent subscription document
            return subs.sort(byLatest)[0];
          };

          const subscriptionUnsubscribe = onSnapshot(subscriptionsRef, (snapshot) => {
            if (!snapshot.empty) {
              const best = pickBestSubscription(snapshot.docs);
              const status = best?.status || 'none';
              const tier = (status === 'active' || status === 'trialing') ? 'pro' : 'free';

              const trialEndSecNorm = normalizeToSeconds(best?.trial_end);
              const currentPeriodEndSecNorm = normalizeToSeconds(best?.current_period_end);

              setSubscriptionStatus({
                status,
                tier,
                trialEndSec: trialEndSecNorm,
                currentPeriodEndSec: currentPeriodEndSecNorm,
                trialEnd: trialEndSecNorm ? new Date(trialEndSecNorm * 1000).toISOString() : null,
                currentPeriodEnd: currentPeriodEndSecNorm ? new Date(currentPeriodEndSecNorm * 1000).toISOString() : null,
                cancelAtPeriodEnd: !!best?.cancel_at_period_end
              });

              // Force-refresh the Firebase JWT so the hasSubscription custom claim
              // (set by the setUserClaims Cloud Function) is included in the token.
              // This ensures the getResearchArticles edge function sees the claim
              // immediately rather than waiting up to 60 min for natural token expiry.
              // 3s delay gives the Cloud Function time to finish setCustomUserClaims.
              if (status === 'active' || status === 'trialing') {
                setTimeout(() => {
                  getAuth().currentUser?.getIdToken(true).catch(() => {});
                }, 3000);
              }

              analyticsInputsRef.current.stripeStatus = status;
            } else {
              setSubscriptionStatus({ status: 'none', tier: 'free' });
              analyticsInputsRef.current.stripeStatus = 'none';
            }

            refreshSubscriptionStatusSuperProperty(
              analyticsInputsRef.current.userData,
              analyticsInputsRef.current.stripeStatus
            );

            setLoading(false);
          }, (error) => {
            console.error('Error in subscription listener:', error);
            setSubscriptionStatus({
              status: 'none',
              tier: 'free'
            });
            setLoading(false);
          });
          
          // Store cleanup function for these listeners
          firestoreCleanupRef.current = () => {
            userUnsubscribe();
            subscriptionUnsubscribe();
          };
          
        } catch (error) {
          console.error('Error setting up Firestore listeners:', error);
          setSubscriptionStatus({
            status: 'none',
            tier: 'free'
          });
          setIsGrandfathered(false);
          setComplimentaryAccess(EMPTY_COMPLIMENTARY_ACCESS);
          setUserDocError(true);
          setLoading(false);
        }
      } else {
        persistedUser = null;
        isAuthInitialized = false;
        // Picks made from here on are a visitor's (STACK-587).
        syncCheckerPicksWithUser(null);

        setCurrentUser(null);
        setSubscriptionStatus({
          status: 'none',
          tier: 'free'
        });
        setIsGrandfathered(false);
        setComplimentaryAccess(EMPTY_COMPLIMENTARY_ACCESS);
        setUserDocError(false);
        prevGrandfatheredRef.current = null;
        setLoading(false);
      }
    });

    authUnsubscribeRef.current = unsubscribe;

    return () => {
      clearTimeout(loadingTimeout);
      if (firestoreCleanupRef.current) {
        firestoreCleanupRef.current();
      }
      if (authUnsubscribeRef.current) {
        authUnsubscribeRef.current();
      }
    };
  }, []);

  // iOS shell only: checkout happens in Safari (guideline 3.1.1 link-out), so
  // when the user returns to the app the JWT may still lack the hasSubscription
  // claim set while they were away. Force-refresh the token on foreground; the
  // Firestore subscription onSnapshot above resyncs subscriptionStatus itself.
  useEffect(() => {
    if (!isIOSShell()) return undefined;

    let lastRefresh = 0;
    const refreshToken = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastRefresh < 10000) return; // visibilitychange + focus both fire
      lastRefresh = now;
      getAuth().currentUser?.getIdToken(true).catch(() => {});
    };

    document.addEventListener('visibilitychange', refreshToken);
    window.addEventListener('focus', refreshToken);
    return () => {
      document.removeEventListener('visibilitychange', refreshToken);
      window.removeEventListener('focus', refreshToken);
    };
  }, []);

  const logout = () => {
    isAuthInitialized = false;
    persistedUser = null;
    return signOut(getAuth());
  };

  const value = {
    currentUser,
    subscriptionStatus,
    isGrandfathered,
    complimentaryAccess,
    userDocError,
    loading,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthContext; 